import { dictionaries } from "../lib/i18n";
import type { Connection, UiLang } from "../lib/types";

/**
 * Det som skjer *mellom* stoppene og som ingen forteller deg om.
 *
 * To ting overrasker tilreisende på norske fjellveier, og begge ligger i
 * mellomrommet der planen tidligere ikke sa noe:
 *
 * Det første er temperaturen. Man kjører fra Skjolden i tjueto grader og
 * t-skjorte og er på Sognefjellet en time senere, tusen meter høyere, i seks
 * grader og vind. Fallet er ikke vær — det er fysikk, drøyt en halv grad per
 * hundre meter — og det er regnbart på forhånd fra høydedataene appen
 * allerede har.
 *
 * Det andre er tunnelene. Lærdalstunnelen er tjuefire og en halv kilometer,
 * tjue minutter under fjellet uten mobildekning. Den som ikke vet det på
 * forhånd, oppdager det et stykke inne.
 *
 * Begge regnes her og ikke på serveren fordi de er ren utledning av data som
 * allerede er i svaret: høyde per stopp og tunnellista per etappe. Et
 * ekstra felt i API-et ville bare vært samme regnestykke, gjort et annet sted.
 */

/** Standard temperaturgradient i fri atmosfære: 0,65 °C per 100 meter. */
const LAPSE_RATE_PER_100M = 0.65;

/**
 * Stigningen der temperaturfallet er verdt å nevne.
 *
 * 700 meter er rundt fire og en halv grad — nok til at en t-skjorte blir
 * feil, og nok til at man merker det når man går ut av bilen. Under det er
 * det en detalj, og en advarsel per etappe ville lært brukeren å se forbi dem.
 */
const CLIMATE_SHOCK_ASCENT_M = 700;

/** Over dette er man på fjellet uansett hvor slakt man kom seg dit. */
const HIGH_ALTITUDE_M = 1000;

/** Under dette er en tunnel et ikke-tema. */
const LONG_TUNNEL_KM = 5;

export type RouteAlertKind = "climate" | "tunnel";

export interface RouteAlert {
  kind: RouteAlertKind;
  title: string;
  detail: string;
}

/**
 * Varslene for én etappe, i den rekkefølgen de møter deg.
 *
 * `toElevationM` kommer fra stoppet etappen ender på; `connection.ascentM` er
 * høydeforskjellen med fortegn. Et fall på ni hundre meter gir ingen advarsel
 * — man kjører ned i varmen, og det er ingen som blir stående og fryse av.
 */
export function routeAlerts(
  connection: Connection,
  toElevationM: number,
  lang: UiLang,
): RouteAlert[] {
  const t = dictionaries[lang];
  const alerts: RouteAlert[] = [];

  const climbs = connection.ascentM >= CLIMATE_SHOCK_ASCENT_M;
  const high = toElevationM >= HIGH_ALTITUDE_M && connection.ascentM > 0;

  if (climbs || high) {
    const drop = Math.round((connection.ascentM / 100) * LAPSE_RATE_PER_100M);
    alerts.push({
      kind: "climate",
      title: t.alertClimateTitle,
      detail: t.alertClimateBody(connection.ascentM, toElevationM, Math.max(drop, 1)),
    });
  }

  for (const tunnel of connection.tunnels) {
    if (tunnel.lengthKm < LONG_TUNNEL_KM) continue;
    alerts.push({
      kind: "tunnel",
      title: t.alertTunnelTitle(tunnel.name, tunnel.lengthKm),
      // Minutter ved 80 km/t, som er fartsgrensen i de fleste av dem.
      detail: `${t.alertTunnelBody(tunnel.road, Math.round((tunnel.lengthKm / 80) * 60))} ${tunnel.note}`,
    });
  }

  return alerts;
}
